import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { customInstance } from "@/lib/api/custom-instance";
import { logError, logInfo } from "@/lib/structured-log";

export interface WorkspaceCompanyProfile {
  id: string | null;
  workspace_id: string;
  name: string;
  website: string;
  location: string;
  founded_in: number | null;
  business_type: string;
  description: string;
  target_audience: string;
  selling_points: string[];
  products: string[];
  logo: string | null;
  logo_url?: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface WorkspaceCompanyProfileInput {
  name: string;
  website: string;
  location: string;
  founded_in: number | null;
  business_type: string;
  description: string;
  target_audience: string;
  selling_points: string[];
  products: string[];
  logo: string | null;
}

export interface WorkspaceCompanyProfileLogoSignature {
  upload_url: string;
  form_fields: Record<string, string>;
  object_key: string;
  expire_time: number;
}

const workspaceIdSchema = z.object({
  workspaceId: z.string(),
});

const previewCompanyProfileSchema = workspaceIdSchema.extend({
  website: z.string().min(1),
  language: z.string().optional(),
});

const saveCompanyProfileSchema = workspaceIdSchema.extend({
  profile: z.object({
    name: z.string(),
    website: z.string(),
    location: z.string(),
    founded_in: z.number().nullable(),
    business_type: z.string(),
    description: z.string(),
    target_audience: z.string(),
    selling_points: z.array(z.string()),
    products: z.array(z.string()),
    logo: z.string().nullable(),
  }),
});

const logoSignatureSchema = workspaceIdSchema.extend({
  filename: z.string().min(1),
});

interface ApiResponse<TData> {
  data: TData;
  status: number;
}

function extractProfileErrorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof Error)) {
    return fallback;
  }

  try {
    const parsed = JSON.parse(error.message) as { detail?: unknown; message?: unknown };
    const value = parsed.detail ?? parsed.message;
    return typeof value === "string" ? value : fallback;
  } catch {
    return error.message || fallback;
  }
}

export const getWorkspaceCompanyProfileServer = createServerFn({ method: "GET" })
  .validator(workspaceIdSchema)
  .handler(async ({ data }): Promise<WorkspaceCompanyProfile | null> => {
    const response = await customInstance<ApiResponse<WorkspaceCompanyProfile | null>>(
      `/api/workspaces/${data.workspaceId}/company-profile`,
      {
        method: "GET",
      },
    );

    if (response.status !== 200) {
      logError("workspace_company_profile.get.failed", {
        workspace_id: data.workspaceId,
        status: response.status,
      });
      throw new Error("Failed to get workspace company profile");
    }

    return response.data;
  });

export const previewWorkspaceCompanyProfileServer = createServerFn({ method: "POST" })
  .validator(previewCompanyProfileSchema)
  .handler(async ({ data }): Promise<WorkspaceCompanyProfile> => {
    logInfo("workspace_company_profile.preview.started", {
      workspace_id: data.workspaceId,
      website: data.website,
    });

    try {
      const response = await customInstance<ApiResponse<WorkspaceCompanyProfile>>(
        `/api/workspaces/${data.workspaceId}/company-profile/preview`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            website: data.website,
            language: data.language,
          }),
        },
      );

      if (response.status !== 200) {
        logError("workspace_company_profile.preview.failed", {
          workspace_id: data.workspaceId,
          status: response.status,
        });
        throw new Error("Failed to preview workspace company profile");
      }

      logInfo("workspace_company_profile.preview.completed", {
        workspace_id: data.workspaceId,
      });
      return response.data;
    } catch (error) {
      const message = extractProfileErrorMessage(
        error,
        "Failed to preview workspace company profile",
      );
      logError("workspace_company_profile.preview.error", {
        workspace_id: data.workspaceId,
        error: message,
      });
      throw new Error(message);
    }
  });

export const saveWorkspaceCompanyProfileServer = createServerFn({ method: "POST" })
  .validator(saveCompanyProfileSchema)
  .handler(async ({ data }): Promise<WorkspaceCompanyProfile> => {
    const response = await customInstance<ApiResponse<WorkspaceCompanyProfile>>(
      `/api/workspaces/${data.workspaceId}/company-profile`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data.profile),
      },
    );

    if (response.status !== 200) {
      logError("workspace_company_profile.save.failed", {
        workspace_id: data.workspaceId,
        status: response.status,
      });
      throw new Error("Failed to save workspace company profile");
    }

    logInfo("workspace_company_profile.save.completed", {
      workspace_id: data.workspaceId,
      profile_id: response.data.id,
    });
    return response.data;
  });

export const getWorkspaceCompanyProfileLogoSignatureServer = createServerFn({ method: "POST" })
  .validator(logoSignatureSchema)
  .handler(async ({ data }): Promise<WorkspaceCompanyProfileLogoSignature> => {
    const response = await customInstance<ApiResponse<WorkspaceCompanyProfileLogoSignature>>(
      `/api/workspaces/${data.workspaceId}/company-profile/logo/signature?filename=${encodeURIComponent(data.filename)}`,
      {
        method: "POST",
      },
    );

    if (response.status !== 200) {
      logError("workspace_company_profile.logo.signature.failed", {
        workspace_id: data.workspaceId,
        status: response.status,
      });
      throw new Error("Failed to get workspace company profile logo upload signature");
    }

    logInfo("workspace_company_profile.logo.signature.completed", {
      workspace_id: data.workspaceId,
      object_key: response.data.object_key,
    });
    return response.data;
  });
